"use client";

import Link from "next/link";
import { useAdmin } from "@/lib/admin/context";
import { useT } from "@/lib/i18n/context";
import { Icon } from "@/lib/icons/Icon";
import type { WPathContent } from "@/lib/api/cms-window-content";
import type { BreadcrumbItem } from "@/lib/cms/breadcrumb";
import { wPathTranslations } from "./WPath.i18n";
import styles from "./WPath.module.css";

/**
 * WPath — komponent breadcrumbs (typ `w_path`).
 *
 * **Data flow (BEZ dodatkowego endpointa):**
 *   - Trasa breadcrumb (`pageBreadcrumb: BreadcrumbItem[]`) jest server-side
 *     budowana w `[locale]/page.tsx` + `[...slug]/page.tsx` z danych już
 *     dostępnych w bootstrap response (`catalog_content.catalog_path` LUB
 *     `response_catalog_data.path` override) i trafia do `AdminContext`.
 *   - Komponent czyta trail z `useAdmin().pageBreadcrumb`.
 *   - Backend `W_PATH_CONTROLLER::getApiContent()` zwraca WYŁĄCZNIE config
 *     per-instancja okna (microdata, latest_el_clickable, first_el_name) —
 *     ten content trafia tu jako `content` prop (przez WindowRenderer + use()).
 *
 * **Convention:** developer komponentu katalogu (`C_*_CONTROLLER`) odpowiada za
 * dodanie niestandardowych entries do trasy w ramach swojego `getApiContent()`
 * (np. c_news article view dorzuca tytuł artykułu na końcu).
 *
 * **Config behavior:**
 *   - `microdata=true` -> emit `<script type="application/ld+json">` z BreadcrumbList
 *     (schema.org). SEO friendly.
 *   - `latest_el_clickable=false` (default) -> ostatni element renderowany jako
 *     `<span aria-current="page">` (semantyczne "tu jesteś"). True -> ostatni
 *     jako `<Link>` (legacy behavior — admin może chcieć).
 *   - `first_el_name` (string|null) -> nadpisuje nazwę pierwszego elementu
 *     ("Strona główna" -> np. "Główna").
 *
 * **Hidden when empty:** gdy `pageBreadcrumb` jest puste (defensywne — nie
 * powinno się zdarzyć przy poprawnym serwer-side build'zie) -> komponent zwraca
 * null. Inaczej zawsze pokazuje przynajmniej "Strona główna" (dla homepage
 * trail = [home]).
 */
export function WPath({ content }: WPathProps) {
  const { pageBreadcrumb } = useAdmin();
  const t = useT(wPathTranslations);

  if (!pageBreadcrumb || pageBreadcrumb.length === 0) return null;

  /* Override pierwszego elementu (zazwyczaj "Strona główna") gdy admin
     skonfigurował `first_el_name` w panelu okna. */
  const firstElName = content?.config?.first_el_name?.trim() || null;
  const microdata = content?.config?.microdata === true;
  const latestElClickable = content?.config?.latest_el_clickable === true;

  const items: BreadcrumbItem[] = pageBreadcrumb.map((it, i) =>
    i === 0 && firstElName
      ? { ...it, name: firstElName, title: firstElName }
      : it,
  );

  const lastIndex = items.length - 1;

  return (
    <nav className={styles.root} aria-label={t.ariaLabel}>
      <ol className={styles.list}>
        {items.map((item, idx) => {
          const isLast = idx === lastIndex;
          /* Pierwszy element to zawsze Home (z `buildBreadcrumbTrail`) —
             dorzucamy ikonkę domku. Jeśli admin podał `first_el_name`,
             tekst jest zastąpiony ale ikonka zostaje (semantyka "powrót do
             strony głównej"). */
          const isHome = idx === 0;
          const renderInner = () => (
            <>
              {isHome && (
                <Icon
                  name="house"
                  weight="regular"
                  className={styles.iconHome}
                />
              )}
              <span className={styles.text}>{item.name}</span>
            </>
          );

          return (
            <li key={`${idx}-${item.href}`} className={styles.item}>
              {isLast && !latestElClickable ? (
                <span
                  className={`${styles.entry} ${styles.entryActive}`}
                  aria-current="page"
                  title={item.title}
                >
                  {renderInner()}
                </span>
              ) : (
                <Link
                  href={item.href}
                  className={`${styles.entry} ${styles.link} ${isLast ? styles.entryActive : ""}`}
                  aria-current={isLast ? "page" : undefined}
                  title={item.title}
                >
                  {renderInner()}
                </Link>
              )}
              {!isLast && (
                <Icon
                  name="caret-right"
                  weight="bold"
                  className={styles.separator}
                />
              )}
            </li>
          );
        })}
      </ol>

      {/* JSON-LD BreadcrumbList — emitowane TYLKO gdy admin włączył microdata.
          Schema.org standard (zastąpił legacy data-vocabulary.org z PHP template).
          Pomaga search engines (Google, Bing) zrozumieć strukturę nawigacyjną
          strony — może pokazać breadcrumb w wynikach wyszukiwania. */}
      {microdata && items.length > 0 && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: items.map((item, idx) => ({
                "@type": "ListItem",
                position: idx + 1,
                name: item.name,
                item: item.href,
              })),
            }),
          }}
        />
      )}
    </nav>
  );
}

export type WPathProps = {
  /** Config okna z `W_PATH_CONTROLLER::getApiContent()`. NULL = backend nie
      zwrócił danych (np. brak class / method) — komponent dalej działa z
      domyślną konfiguracją (microdata=false, latest_el_clickable=false). */
  content: WPathContent | null;
};
